import * as Tone from "tone"
import toWav from "audiobuffer-to-wav"
import {
    ALIEN_CHORUS,
    DELICATE_WIND_PART,
    DROP_PULSE,
    LECTRIC,
    MARIMBA,
    STEELPAN,
    SUPER_SAW,
    TREE_TRUNK,
    GLASS_HARMONY,
    COSMIC_PAD,
    DIGITAL_BELL,
    WARM_STRINGS,
    ELECTRIC_PIANO,
    SYNTH_BASS,
    CRYSTAL_KEYS,
    DEEP_ORGAN,
    BRIGHT_TRUMPET,
    SOFT_FLUTE,
    METALLIC_DRUM,
    SPACE_VOICE,
    ELECTRO_HARP,
    VINTAGE_ORGAN,
    FUTURE_PAD,
    DIGITAL_CHIME,
} from "../sounds/synth/Miscellaneous.js"
import {
    BAH,
    BASS_GUITAR,
    BASSY,
    BRASS_CIRCUIT,
    COOL_GUY,
    PIANOETTA,
    PIZZ,
    NICE_BOY,
} from "../sounds/monosynth/Miscellaneous.js"
import {
    KALIMBA,
    ELECTRIC_CELLO,
    THIN_SAWS,
    ROUGH_FLY,
    ROUGH_MAN,
    ROUGH_BUILDING,
} from "../sounds/fmsynth/Miscellaneous.js"
import { FLUTE } from "../sounds/samples/NoteToSampleMaps/FluteNoteToSample.js"
import { CELLO } from "../sounds/samples/NoteToSampleMaps/CelloNoteToSample.js"
import { PIANO } from "../sounds/samples/NoteToSampleMaps/PianoNoteToSample.js"
import { PIANO_SALAMANDER } from "../sounds/samples/NoteToSampleMaps/PianoSalamanderUltimate.js"
import { VIOLIN } from "../sounds/samples/NoteToSampleMaps/ViolinNoteToSample.js"
import { TRUMPET } from "../sounds/samples/NoteToSampleMaps/TrumpetNoteToSample.js"
import { TRUMPET_VSCO2, TRUMPET_VSCO2_ARTICULATIONS } from "../sounds/samples/NoteToSampleMaps/TrumpetVSCO2.js"
import { GUITAR_ACOUSTIC } from "../sounds/samples/NoteToSampleMaps/GuitarAcousticNoteToSample.js"
import { GUITAR_ELECTRIC } from "../sounds/samples/NoteToSampleMaps/GuitarElectricNoteToSample.js"
import { BASS_ELECTRIC } from "../sounds/samples/NoteToSampleMaps/BassElectricNoteToSample.js"
import {
    showToastInfo,
    showToastSuccess,
    showToastError,
} from "../style/ShowToast.js"

// Mapping between instrument names and their corresponding noteToSampleNameMap
const instrumentToSampleMap = {
    Cello: CELLO,
    Flute: FLUTE,
    Piano: PIANO,
    "Piano Salamander": PIANO_SALAMANDER,
    Violin: VIOLIN,
    Trumpet: TRUMPET,
    "Trumpet VSCO2": TRUMPET_VSCO2,
    "Guitar acoustic": GUITAR_ACOUSTIC,
    "Guitar electric": GUITAR_ELECTRIC,
    "Bass electric": BASS_ELECTRIC,
    // Add other instruments and their mappings here
}

const instrumentToSynthConfigMap = {
    "Alien chorus": ALIEN_CHORUS,
    "Delicate wind part": DELICATE_WIND_PART,
    "Drop pulse": DROP_PULSE,
    Lectric: LECTRIC,
    Marimba: MARIMBA,
    Steelpan: STEELPAN,
    "Super saw": SUPER_SAW,
    "Tree trunk": TREE_TRUNK,
    "Glass harmony": GLASS_HARMONY,
    "Cosmic pad": COSMIC_PAD,
    "Digital bell": DIGITAL_BELL,
    "Warm strings": WARM_STRINGS,
    "Electric piano": ELECTRIC_PIANO,
    "Synth bass": SYNTH_BASS,
    "Crystal keys": CRYSTAL_KEYS,
    "Deep organ": DEEP_ORGAN,
    "Bright trumpet": BRIGHT_TRUMPET,
    "Soft flute": SOFT_FLUTE,
    "Metallic drum": METALLIC_DRUM,
    "Space voice": SPACE_VOICE,
    "Electro harp": ELECTRO_HARP,
    "Vintage organ": VINTAGE_ORGAN,
    "Future pad": FUTURE_PAD,
    "Digital chime": DIGITAL_CHIME,
}

const instrumentToMonosynthConfigMap = {
    Bah: BAH,
    "Bass guitar": BASS_GUITAR,
    Bassy: BASSY,
    "Brass circuit": BRASS_CIRCUIT,
    "Cool guy": COOL_GUY,
    Pianoetta: PIANOETTA,
    Pizz: PIZZ,
    "Nice boy": NICE_BOY,
}

const instrumentToFMSynthConfigMap = {
    Kalimba: KALIMBA,
    "Electric cello": ELECTRIC_CELLO,
    "Thin saws": THIN_SAWS,
    "Rough fly": ROUGH_FLY,
    "Rough man": ROUGH_MAN,
    "Rough building": ROUGH_BUILDING,
}

function formatInstrumentName(name) {
    return name.toLowerCase().replace(" ", "_")
}

export const getSynth = async (category, instrumentName) => {
    switch (category) {
        case "Samples":
            const noteToSampleMapping = instrumentToSampleMap[instrumentName]
            const formattedInstumentName = formatInstrumentName(instrumentName)
            const samplesFolderUrl = process.env.REACT_APP_SAMPLES_URL

            if (noteToSampleMapping) {
                // Special handling for Salamander Piano with professional effects
                if (instrumentName === "Piano Salamander") {
                    const piano = new Tone.Sampler({
                        urls: noteToSampleMapping,
                        release: 2.0,  // Natural decay - prevents abrupt cutoff
                        attack: 0.001,
                        decay: 0.1,
                        sustain: 0.8,
                        onload: () => {
                            console.log("✓ Salamander Grand Piano loaded - Professional quality!")
                        },
                        baseUrl: `${samplesFolderUrl}/${formattedInstumentName}/`
                    })
                    
                    // Concert hall reverb
                    const reverb = new Tone.Reverb({
                        decay: 2.5,
                        preDelay: 0.01,
                        wet: 0.15
                    }).toDestination()
                    
                    // Professional compression
                    const compressor = new Tone.Compressor({
                        threshold: -18,
                        ratio: 2.5,
                        attack: 0.003,
                        release: 0.25
                    })
                    
                    // EQ for warmth and clarity
                    const eq = new Tone.EQ3({
                        low: 2,
                        mid: 0,
                        high: 1.5,
                        lowFrequency: 200,
                        highFrequency: 4000
                    })
                    
                    // Chain: piano -> compressor -> EQ -> reverb -> output
                    piano.chain(compressor, eq, reverb)
                    
                    return piano
                }
                
                // Special handling for VSCO2 Trumpet with multiple articulations
                if (instrumentName === "Trumpet VSCO2") {
                    // Create samplers for each articulation
                    const samplers = {}
                    const articulationConfigs = {
                        staccato: { release: 0.5, attack: 0.01, decay: 0.1, sustain: 0.7 },
                        sustain: { release: 2.0, attack: 0.01, decay: 0.2, sustain: 0.8 },
                        susvib: { release: 2.5, attack: 0.01, decay: 0.3, sustain: 0.8 },
                        harmonMute: { release: 1.8, attack: 0.01, decay: 0.2, sustain: 0.7 },
                        straightMute: { release: 1.8, attack: 0.01, decay: 0.2, sustain: 0.7 }
                    }
                    
                    // Create a sampler for each articulation
                    for (const [articulationName, mapping] of Object.entries(TRUMPET_VSCO2_ARTICULATIONS)) {
                        const config = articulationConfigs[articulationName] || articulationConfigs.staccato
                        samplers[articulationName] = new Tone.Sampler({
                            urls: mapping,
                            release: config.release,
                            attack: config.attack,
                            decay: config.decay,
                            sustain: config.sustain,
                            baseUrl: `${samplesFolderUrl}/${formattedInstumentName}/${articulationName}/`
                        }).toDestination()
                    }
                    
                    // Create a smart wrapper that switches based on articulation
                    const smartSampler = {
                        samplers: samplers,
                        triggerAttackRelease: (note, duration, time, velocity, articulation = "sustain") => {
                            // Normalize articulation name (handle variations)
                            let normalizedArticulation = articulation
                            if (articulation === "stac") normalizedArticulation = "staccato"
                            if (articulation === "sus") normalizedArticulation = "sustain"
                            if (articulation === "harmonM-sus" || articulation === "harmonMute") normalizedArticulation = "harmonMute"
                            if (articulation === "straightM-sus" || articulation === "straightMute") normalizedArticulation = "straightMute"
                            
                            // Get the appropriate sampler (default to sustain if not found)
                            const sampler = samplers[normalizedArticulation] || samplers["sustain"]
                            
                            // Use note name (string) for samplers
                            const noteName = typeof note === "string" ? note : Tone.Frequency(note).toNote()
                            sampler.triggerAttackRelease(noteName, duration, time, velocity)
                        },
                        toDestination: () => {
                            // Connect all samplers to destination
                            Object.values(samplers).forEach(sampler => sampler.toDestination())
                            return smartSampler
                        },
                        volume: (() => {
                            // Create a volume proxy that forwards all methods to all samplers
                            const volumeProxy = {
                                value: 0,
                                setValueAtTime: (value, time) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.setValueAtTime(value, time)
                                    })
                                    volumeProxy.value = value
                                },
                                rampTo: (value, rampTime) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.rampTo(value, rampTime)
                                    })
                                    volumeProxy.value = value
                                },
                                linearRampTo: (value, rampTime) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.linearRampTo(value, rampTime)
                                    })
                                    volumeProxy.value = value
                                },
                                exponentialRampTo: (value, rampTime) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.exponentialRampTo(value, rampTime)
                                    })
                                    volumeProxy.value = value
                                },
                                cancelScheduledValues: (time) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.cancelScheduledValues(time)
                                    })
                                },
                                setValueCurveAtTime: (values, startTime, duration) => {
                                    Object.values(samplers).forEach(sampler => {
                                        sampler.volume.setValueCurveAtTime(values, startTime, duration)
                                    })
                                }
                            }
                            return volumeProxy
                        })(),
                        dispose: () => {
                            Object.values(samplers).forEach(sampler => sampler.dispose())
                        }
                    }
                    
                    // Set default volume (samplers already connected via .toDestination() above)
                    smartSampler.volume.value = 0
                    Object.values(samplers).forEach(sampler => {
                        sampler.volume.value = 0
                    })
                    
                    console.log("✓ VSCO2 Trumpet loaded with all articulations - High quality!")
                    
                    return smartSampler
                }
                
                // Standard sampler for other instruments
                return new Tone.Sampler(
                    noteToSampleMapping,
                    null,
                    `${samplesFolderUrl}/${formattedInstumentName}/`
                ).toDestination()
            }
            break
        case "Synth":
            return new Tone.Synth({
                ...instrumentToSynthConfigMap[instrumentName],
            }).toDestination()
            break
        case "Monosynth":
            return new Tone.MonoSynth({
                ...instrumentToMonosynthConfigMap[instrumentName],
            }).toDestination()
            break
        case "FMSynth":
            return new Tone.MonoSynth({
                ...instrumentToFMSynthConfigMap[instrumentName],
            }).toDestination()
            break
        default:
            return new Tone.Synth().toDestination()
    }
}

export const loadSynth = async (instrument) => {
    if (!instrument) {
        return
    }

    const { value, category } = instrument

    const synth = await getSynth(category, value)

    // wait for it to load
    await Tone.loaded()

    synth.volume.value = instrument.volume

    return synth
}

export const disposeSynths = (synths) => {
    synths.forEach((synth) => synth.dispose())
}

export const handleWAVDownloadClick = async (midi, selectedInstruments) => {
    try {
        const toastInfo = showToastInfo(
            "Preparing your WAV file for download..."
        )

        const renderedBuffer = await getRenderedBuffer(
            midi,
            selectedInstruments
        )

        downloadWavFile(renderedBuffer)
        toastInfo.dismiss()
        showToastSuccess("Your WAV file is ready for download!", 5000)
    } catch (error) {
        console.error("An error occurred:", error)
        showToastError("An error occurred while preparing your WAV file.", 5000)
    }
}

export const getRenderedBuffer = async (midi, selectedInstruments) => {
    const totalDuration = midi.duration
    const renderedBuffer = await Tone.Offline(async (offlineContext) => {
        const offlineSynths = await loadAllSynths(
            midi.tracks,
            selectedInstruments,
            offlineContext
        )
        triggerAllNotes(midi.tracks, offlineSynths)
    }, totalDuration)

    return renderedBuffer
}

export const loadAllSynths = async (
    midiTracks,
    selectedInstruments,
    offlineContext
) => {
    return await Promise.all(
        midiTracks.map(async (track, i) => {
            const instrument = selectedInstruments[i]
            const synth = await loadSynth(instrument, false)
            if (synth) {
                synth.context = offlineContext
            } else {
                console.error("Synth not loaded for instrument:", instrument)
            }
            return synth
        })
    )
}

// Helper to trigger all notes
const triggerAllNotes = (midiTracks, offlineSynths) => {
    for (let i = 0; i < midiTracks.length; i++) {
        const track = midiTracks[i]
        const offlineSynth = offlineSynths[i]
        if (offlineSynth) {
            for (const note of track.notes) {
                // Get articulation from note (default to "sustain" if not present)
                const articulation = note.articulation || "sustain"
                
                // Check if this is a smart sampler
                if (offlineSynth.samplers) {
                    // Smart sampler - pass articulation
                    offlineSynth.triggerAttackRelease(
                        note.name,
                        note.duration,
                        note.time,
                        0.2,
                        articulation
                    )
                } else {
                    // Standard sampler
                    offlineSynth.triggerAttackRelease(
                        note.name,
                        note.duration,
                        note.time,
                        0.2
                    )
                }
            }
        }
    }
}

// Function to create a WAV Blob and trigger a download
const downloadWavFile = (renderedBuffer) => {
    const wav = toWav(renderedBuffer)
    const blob = new Blob([wav], { type: "audio/wav" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = "output.wav"
    a.click()

    // Try this:
    // // Revoke the URL after a few seconds to free up resources
    // setTimeout(() => {
    //     URL.revokeObjectURL(url)
    // }, 10000) // 10000 milliseconds = 10 seconds
}

// export const createBufferedMidiEvents = async (tracks, activeSynths, selectedInstruments) => {
//     const hasSolo = selectedInstruments.some((instrument) => instrument.isSolo)
//     let parts = []
//     let maxDuration = 0

//     for (let i = 0; i < tracks.length; i++) {
//         const track = tracks[i]
//         const trackSynth = activeSynths[i]
//         const instrument = selectedInstruments[i]
//         const volume = getTrackVolume(trackSynth, instrument, hasSolo)

//         const part = new Tone.Part((time, note) => {
//             trackSynth.triggerAttackRelease(
//                 note.name,
//                 note.duration,
//                 time,
//                 volume
//             )
//         }, track.notes)

//         parts.push(part)

//         maxDuration = Math.max(maxDuration, track.duration)
//     }

//     return { parts, maxDuration }
// }

export const createBufferedMidiEvents = async (tracks, activeSynths, selectedInstruments, tuning) => { 
    const hasSolo = selectedInstruments.some((instrument) => instrument.isSolo);
    let parts = [];
    let maxDuration = 0;

    for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        const trackSynth = activeSynths[i];
        const instrument = selectedInstruments[i];
        const volume = getTrackVolume(trackSynth, instrument, hasSolo);

        const part = new Tone.Part((time, note) => {
            const pitchClass = note.name.split(/\d/)[0];
            const deviation = tuning[pitchClass] || 0;
            
            // For Samplers, use note names instead of frequencies
            let noteToPlay = note.name;
            if (deviation !== 0) {
                // Convert cents to semitones (deviation is in cents, transpose expects semitones)
                const semitones = deviation / 100;
                const adjustedNote = Tone.Frequency(note.name).transpose(semitones);
                noteToPlay = adjustedNote.toNote();
            }
            
            // Get articulation from note (default to "sustain" if not present)
            const articulation = note.articulation || "sustain";
            
            // Check if this is a smart sampler (has samplers property)
            if (trackSynth.samplers) {
                // This is a smart sampler - pass articulation
                trackSynth.triggerAttackRelease(noteToPlay, note.duration, time, volume, articulation);
            } else {
                // Standard sampler - use frequency or note name
                if (typeof trackSynth.triggerAttackRelease === 'function') {
                    // Try note name first (for samplers)
                    try {
                        trackSynth.triggerAttackRelease(noteToPlay, note.duration, time, volume);
                    } catch (e) {
                        // Fallback to frequency if note name doesn't work
                        const baseFrequency = Tone.Frequency(note.name).toFrequency();
                        const adjustedFrequency = baseFrequency * Math.pow(2, deviation / 1200);
                        trackSynth.triggerAttackRelease(adjustedFrequency, note.duration, time, volume);
                    }
                }
            }
        }, track.notes);

        parts.push(part);

        maxDuration = Math.max(maxDuration, track.duration);
    }

    return { parts, maxDuration };
};

export const getTuning = (style) => {
    const pitchClasses = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    const tuning = {};

    if (!style) {
        return tuning
    }

    style.forEach(item => {
        if (pitchClasses.includes(item.name)) {
            tuning[item.name] = item.value;
        }
    });

    return tuning;
};

// export const createBufferedMidiEvents = async (tracks, activeSynths, selectedInstruments) => {
//     const hasSolo = selectedInstruments.some((instrument) => instrument.isSolo);
//     let parts = [];
//     let maxDuration = 0;

//     for (let i = 0; i < tracks.length; i++) {
//         const track = tracks[i];
//         const trackSynth = activeSynths[i];
//         const instrument = selectedInstruments[i];
//         const volume = getTrackVolume(trackSynth, instrument, hasSolo);

//         const part = new Tone.Part((time, note) => {
//             const tonicFrequency = 261.63; // Frequency of C4 (tonic)
//             const neutralThirdRatio = 11 / 9; // Ratio for neutral third
//             const neutralThirdFrequency = tonicFrequency * neutralThirdRatio;

//             // Check if the note is an E (any octave)
//             const isE = note.name.startsWith("E"); // Matches E3, E4, E5, etc.

//             // Calculate the frequency: Replace E with the neutral third frequency
//             const frequency = isE
//                 ? neutralThirdFrequency * Math.pow(2, parseInt(note.name.slice(1)) - 4)
//                 : Tone.Frequency(note.name).toFrequency();

//             trackSynth.triggerAttackRelease(
//                 frequency, // Use calculated frequency
//                 note.duration,
//                 time,
//                 volume
//             );
//         }, track.notes);

//         parts.push(part);

//         maxDuration = Math.max(maxDuration, track.duration);
//     }

//     return { parts, maxDuration };
// };

const getTrackVolume = (trackSynth, instrument, hasSolo) => {
    if (hasSolo) {
        return instrument.isSolo && !instrument.isMuted ? trackSynth.volume.value : 0;
    }
    return instrument.isMuted ? 0 : trackSynth.volume.value;
};
